import { Component, Input, Output, EventEmitter, forwardRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Custom star rating component that replaces ng-bootstrap's ngb-rating.
 * Supports read-only and interactive modes, keyboard navigation, and form integration.
 */
@Component({
  selector: 'app-star-rating',
  templateUrl: './star-rating.component.html',
  styleUrls: ['./star-rating.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StarRatingComponent),
      multi: true
    }
  ]
})
export class StarRatingComponent implements ControlValueAccessor {
  /**
   * Current rating value (0 to max)
   */
  @Input() rate: number = 0;

  /**
   * Maximum rating value (default: 5)
   */
  @Input() max: number = 5;

  /**
   * Whether the rating is read-only
   */
  @Input() readonly: boolean = false;

  /**
   * Whether clicking the current rating resets it to 0
   */
  @Input() resettable: boolean = false;

  /**
   * Emits when the rating value changes
   */
  @Output() rateChange = new EventEmitter<number>();

  /**
   * Internal state for hover effect
   */
  hoveredRating: number = 0;

  /**
   * Whether the component is disabled (for form control)
   */
  disabled: boolean = false;

  /**
   * ControlValueAccessor callbacks
   */
  private onChange: (value: number) => void = () => {};
  private onTouched: () => void = () => {};

  /**
   * Get array of star indices for template iteration
   */
  get stars(): number[] {
    return Array.from({ length: this.max }, (_, i) => i + 1);
  }

  /**
   * Check if a star at given index should be filled
   */
  isFilled(index: number): boolean {
    const rating = this.hoveredRating || this.rate;
    return index <= rating;
  }

  /**
   * Handle star click
   */
  onStarClick(index: number): void {
    if (this.readonly || this.disabled) {
      return;
    }

    // If resettable and clicking the current rating, reset to 0
    if (this.resettable && this.rate === index) {
      this.updateRating(0);
    } else {
      this.updateRating(index);
    }
  }

  /**
   * Handle mouse enter on star
   */
  onStarHover(index: number): void {
    if (!this.readonly && !this.disabled) {
      this.hoveredRating = index;
    }
  }

  /**
   * Handle mouse leave from star container
   */
  onMouseLeave(): void {
    this.hoveredRating = 0;
  }

  /**
   * Update rating value and emit changes
   */
  private updateRating(value: number): void {
    this.rate = value;
    this.rateChange.emit(this.rate);
    this.onChange(this.rate);
    this.onTouched();
  }

  /**
   * Keyboard navigation support
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.readonly || this.disabled) {
      return;
    }

    let newRating = this.rate;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        newRating = Math.min(this.rate + 1, this.max);
        event.preventDefault();
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        newRating = Math.max(this.rate - 1, 0);
        event.preventDefault();
        break;
      case ' ':
      case 'Enter':
        // Confirm current rating (no change needed)
        event.preventDefault();
        break;
      case 'Escape':
        // Could reset to original value, but we'll just blur
        (event.target as HTMLElement).blur();
        event.preventDefault();
        break;
      default:
        return;
    }

    if (newRating !== this.rate) {
      this.updateRating(newRating);
    }
  }

  // ControlValueAccessor implementation

  writeValue(value: number): void {
    this.rate = value || 0;
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
